import { useMemo } from "react";
import { z } from "zod";
import {
  Box,
  Skeleton,
  Text,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import { ActivityItem } from "#app/components/issue-view/activity/activity-item";
import { ActivityProvider } from "#app/components/issue-view/activity/activity-context";
import { NoteItem } from "#app/components/issue-view/activity/note-item";
import { NoteComposer } from "#app/components/issue-view/activity/note-composer";
import { NoteThread } from "#app/components/issue-view/activity/note-thread";
import type { NoteNode } from "#app/components/issue-view/activity/activity-types";
import { useIssueWidgetContext } from "#app/widgets/issue/issue-widget-context";

export const issueCommentsConfigSchema = z.looseObject({ limit: z.number().default(3) });
export type IssueCommentsConfig = z.infer<typeof issueCommentsConfigSchema>;

function noteTimestamp(note: NoteNode): number {
  return new Date(note.lastUpdateDate ?? note.creationDate).getTime();
}

function IssueCommentsRender({ config }: WidgetRenderContext<IssueCommentsConfig>) {
  const ctx = useIssueWidgetContext();
  const issueId = ctx.issue?.issueId;
  // The latest N notes (threads and replies alike), newest first. An age visit
  // whose note rides a combined activity renders as that ActivityItem so the
  // stage/assignee chips come with it.
  const rows = useMemo(() => {
    const notesById = new Map<string, NoteNode>();
    const ageActivityByNoteId = new Map(
      (ctx.activity?.activities ?? []).flatMap((activity) => {
        const noteId = activity.changeset?.age?.after?.noteId;
        return noteId ? [[noteId, activity] as const] : [];
      }),
    );

    for (const note of ctx.notes ?? []) {
      notesById.set(note.noteId, note);
      for (const reply of note.replies ?? []) {
        notesById.set(reply.noteId, reply);
      }
    }

    return Array.from(notesById.values())
      .sort((left, right) => noteTimestamp(right) - noteTimestamp(left))
      .slice(0, config.limit)
      .map((note) => ({ note, ageActivity: ageActivityByNoteId.get(note.noteId) }));
  }, [config.limit, ctx.activity?.activities, ctx.notes]);

  if (ctx.activityLoading && rows.length === 0) {
    return (
      <Box direction="column" gap="sm">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-18 w-full" />
        ))}
      </Box>
    );
  }

  if (!issueId || !ctx.activity) {
    return (
      <Box paddingY="sm">
        <Text size="sm" variant="muted">No comments yet</Text>
      </Box>
    );
  }

  return (
    <ActivityProvider value={ctx.activity}>
      <Box border rounded="sm" overflow="hidden" padding="sm" gap="sm">
        {rows.length > 0 ? (
          rows.map(({ note, ageActivity }, index) =>
            ageActivity ? (
              <ActivityItem
                key={`${note.noteId}-${index}`}
                issueId={issueId}
                activity={ageActivity}
                last={index === rows.length - 1}
              />
            ) : note.parentNoteId ? (
              <NoteItem
                key={`${note.noteId}-${index}`}
                issueId={issueId}
                noteId={note.noteId}
              />
            ) : (
              <NoteThread
                key={`${note.noteId}-${index}`}
                issueId={issueId}
                noteId={note.noteId}
              />
            ),
          )
        ) : (
          <Box paddingY="sm">
            <Text size="sm" variant="muted">No comments yet</Text>
          </Box>
        )}
        <NoteComposer issueId={issueId} />
      </Box>
    </ActivityProvider>
  );
}

export const issueCommentsDef: WidgetModule<IssueCommentsConfig> = {
  type: "issue-comments",
  title: "Comments",
  icon: "message-square",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 12, defaultW: 12, defaultH: 4 },
  configSchema: issueCommentsConfigSchema,
  render: IssueCommentsRender,
};
